import type { FC } from 'react';

import type { PropertyContact } from '@/services/types/coverage-types';

interface PremisesContactSelectorProps {
  contacts: PropertyContact[];
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
}

export const PremisesContactSelector: FC<PremisesContactSelectorProps> = ({
  contacts,
  selectedIdx,
  onSelect,
}) => {
  if (contacts.length <= 1) return null;

  return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-lg shadow-zinc-200/60">
      <div className="space-y-3">
        <div>
          <span className="text-sm font-semibold text-zinc-700">
            Select Recipient
          </span>
          <p className="text-xs text-zinc-500 mt-0.5">
            Choose which property contact to send this LOR to
          </p>
        </div>
        <div className="space-y-2">
          {contacts.map((contact, idx) => {
            const isSelected = selectedIdx === idx;
            const contactLabel =
              contact.name ||
              (contact.contact_type === 'PROPERTY_OWNER'
                ? 'Property Owner'
                : contact.contact_type === 'PROPERTY_MANAGER'
                  ? 'Property Manager'
                  : contact.custom_type_label || 'Contact');
            const handles = contact.handles ?? [];
            const emailHandle = handles.find((h) =>
              (h.handle_type || '').toUpperCase().includes('EMAIL'),
            );
            const faxHandle = handles.find((h) =>
              (h.handle_type || '').toUpperCase().includes('FAX'),
            );

            return (
              <button
                key={idx}
                type="button"
                onClick={() => onSelect(idx)}
                className={`w-full rounded-lg border p-3 text-left transition ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50/50'
                    : 'border-zinc-200 bg-white hover:border-zinc-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-800">
                      {contactLabel}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {contact.contact_type === 'PROPERTY_OWNER'
                        ? 'Property Owner'
                        : contact.contact_type === 'PROPERTY_MANAGER'
                          ? 'Property Manager'
                          : contact.custom_type_label || 'Other'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {emailHandle && (
                      <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        Email
                      </span>
                    )}
                    {faxHandle && (
                      <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                          />
                        </svg>
                        Fax
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
